/**
 * Automation Section - Settings
 * Unified view of user-created rules and learned behaviors
 */

import { useState, useEffect } from 'react';
import { IconPlus, IconPencil, IconTrash, IconLoader2, IconBolt, IconToggleLeft, IconToggleRight, IconTag, IconFolderFilled, IconPlayerPlay, IconMail, IconArrowRight, IconInfoCircle } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tooltip } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  fetchRules,
  createRule,
  updateRule,
  deleteRule,
  toggleRule,
  applyRulesToExistingEmails,
  type Rule,
  type RuleActionConfig,
} from '@/lib/cloudflare/rules-client';
import { fetchLabels, type Label as LabelType } from '@/lib/cloudflare/labels-client';
import {
  fetchSenderLabelRules,
  updateSenderLabelRule,
  deleteSenderLabelRule,
  type SenderLabelRule,
} from '@/lib/cloudflare/sender-label-rules-client';
import { preferencesClient, SenderPreference } from '@/lib/cloudflare/preferences-client';
import { useNavigate } from '@tanstack/react-router';

type CategoryType = 'do_nothing' | 'delete' | 'archive' | 'unsubscribe';
type FilterType = 'all' | 'rules' | 'learned';

const CATEGORY_LABELS: Record<string, string> = {
  do_nothing: 'Inbox',
  delete: 'Delete',
  archive: 'Archive',
  unsubscribe: 'Unsubscribe',
};

const CATEGORY_COLORS: Record<CategoryType, string> = {
  do_nothing: 'bg-green-500',
  delete: 'bg-red-500',
  archive: 'bg-blue-500',
  unsubscribe: 'bg-orange-500',
};

export function AutomationSection() {
  const navigate = useNavigate();
  const [rules, setRules] = useState<Rule[]>([]);
  const [senderLabelRules, setSenderLabelRules] = useState<SenderLabelRule[]>([]);
  const [senderPreferences, setSenderPreferences] = useState<SenderPreference[]>([]);
  const [labels, setLabels] = useState<LabelType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');

  // Rule dialog state
  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [isApplyingRules, setIsApplyingRules] = useState(false);

  // Delete confirmation
  const [deleteConfirmRule, setDeleteConfirmRule] = useState<Rule | null>(null);
  const [deleteConfirmSenderLabel, setDeleteConfirmSenderLabel] = useState<SenderLabelRule | null>(null);
  const [deleteConfirmPref, setDeleteConfirmPref] = useState<SenderPreference | null>(null);

  // Rule form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [actionType, setActionType] = useState<'label' | 'category'>('label');
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('archive');

  // Sender label rule edit
  const [editingSenderRule, setEditingSenderRule] = useState<SenderLabelRule | null>(null);
  const [editSenderRuleLabelName, setEditSenderRuleLabelName] = useState('');

  // Sender preference edit
  const [updatingPrefId, setUpdatingPrefId] = useState<string | null>(null);

  const loadData = async () => {
    try {
      const [rulesData, labelsData, senderRulesData, prefsData] = await Promise.all([
        fetchRules(),
        fetchLabels(),
        fetchSenderLabelRules(),
        preferencesClient.getPreferences(),
      ]);
      setRules(rulesData);
      setLabels(labelsData.labels);
      setSenderLabelRules(senderRulesData);
      setSenderPreferences(prefsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load automation data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Rule handlers
  const openCreateRuleDialog = () => {
    setEditingRule(null);
    setName('');
    setDescription('');
    setActionType('label');
    setSelectedLabelIds([]);
    setSelectedCategory('archive');
    setIsRuleDialogOpen(true);
  };

  const openEditRuleDialog = (rule: Rule) => {
    setEditingRule(rule);
    setName(rule.name);
    setDescription(rule.description);
    setActionType(rule.action_type === 'category' ? 'category' : 'label');
    setSelectedLabelIds(rule.actionConfig.labelIds || []);
    setSelectedCategory(rule.actionConfig.category || 'archive');
    setIsRuleDialogOpen(true);
  };

  const handleRuleSubmit = async () => {
    if (!name.trim() || !description.trim()) {
      toast.error('Name and description are required');
      return;
    }
    if (actionType === 'label' && selectedLabelIds.length === 0) {
      toast.error('Select at least one label');
      return;
    }

    const actionConfig: RuleActionConfig = actionType === 'label'
      ? { labelIds: selectedLabelIds }
      : { category: selectedCategory as CategoryType };

    setIsSaving(true);
    try {
      if (editingRule) {
        const updated = await updateRule(editingRule.id, {
          name: name.trim(),
          description: description.trim(),
          actionType,
          actionConfig,
        });
        setRules(rules.map(r => r.id === updated.id ? { ...r, ...updated } : r));
        toast.success('Rule updated');
      } else {
        const { rule: created } = await createRule({
          name: name.trim(),
          description: description.trim(),
          actionType,
          actionConfig,
        });
        setRules([...rules, created]);
        toast.success('Rule created');
      }
      setIsRuleDialogOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save rule');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRule = async () => {
    if (!deleteConfirmRule) return;
    try {
      await deleteRule(deleteConfirmRule.id);
      setRules(rules.filter(r => r.id !== deleteConfirmRule.id));
      toast.success('Rule deleted');
    } catch (error) {
      toast.error('Failed to delete rule');
    } finally {
      setDeleteConfirmRule(null);
    }
  };

  const handleToggleRule = async (rule: Rule) => {
    setTogglingId(rule.id);
    try {
      const isEnabled = await toggleRule(rule.id);
      setRules(rules.map(r => r.id === rule.id ? { ...r, is_enabled: isEnabled ? 1 : 0 } : r));
    } catch (error) {
      toast.error('Failed to toggle rule');
    } finally {
      setTogglingId(null);
    }
  };

  const handleApplyRules = async () => {
    const enabledRules = rules.filter(r => r.is_enabled);
    if (enabledRules.length === 0) {
      toast.error('No enabled rules to apply');
      return;
    }
    setIsApplyingRules(true);
    try {
      const result = await applyRulesToExistingEmails();
      toast.success(`Applied to ${result.emailsProcessed} emails, ${result.emailsMatched} matched`);
      loadData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to apply rules');
    } finally {
      setIsApplyingRules(false);
    }
  };

  // Sender label rule handlers
  const handleUpdateSenderRule = async () => {
    if (!editingSenderRule || !editSenderRuleLabelName.trim()) return;
    setIsSaving(true);
    try {
      const updated = await updateSenderLabelRule(editingSenderRule.id, editSenderRuleLabelName.trim());
      setSenderLabelRules(senderLabelRules.map(r => r.id === updated.id ? updated : r));
      toast.success('Rule updated');
      setEditingSenderRule(null);
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSenderRule = async () => {
    if (!deleteConfirmSenderLabel) return;
    try {
      await deleteSenderLabelRule(deleteConfirmSenderLabel.id);
      setSenderLabelRules(senderLabelRules.filter(r => r.id !== deleteConfirmSenderLabel.id));
      toast.success('Rule deleted');
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeleteConfirmSenderLabel(null);
    }
  };

  // Sender preference handlers
  const handlePrefCategoryChange = async (pref: SenderPreference, newCategory: CategoryType) => {
    setUpdatingPrefId(pref.id);
    try {
      await preferencesClient.upsertPreference({
        senderPattern: pref.sender_pattern,
        preferredCategory: newCategory,
        source: 'settings_page',
      });
      setSenderPreferences(prefs =>
        prefs.map(p =>
          p.id === pref.id
            ? { ...p, preferred_category: newCategory, source: 'settings_page' }
            : p
        )
      );
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setUpdatingPrefId(null);
    }
  };

  const handleDeletePref = async () => {
    if (!deleteConfirmPref) return;
    try {
      await preferencesClient.deletePreference(deleteConfirmPref.id);
      setSenderPreferences(prefs => prefs.filter(p => p.id !== deleteConfirmPref.id));
      toast.success('Preference deleted');
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeleteConfirmPref(null);
    }
  };

  const getLabelById = (id: string) => labels.find(l => l.id === id);

  // Stats
  const enabledRulesCount = rules.filter(r => r.is_enabled).length;
  const totalMatches = rules.reduce((acc, r) => acc + (r.times_matched || 0), 0);
  const learnedCount = senderLabelRules.length + senderPreferences.length;

  // Filtered items
  const showRules = filter === 'all' || filter === 'rules';
  const showLearned = filter === 'all' || filter === 'learned';

  return (
    <div>
      {/* Header with stats and actions */}
      <div className="flex items-center justify-between border-b border-border/30 pb-2 mb-3">
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span><span className="font-mono text-foreground">{rules.length}</span> rules</span>
          <span><span className="font-mono text-foreground">{learnedCount}</span> learned</span>
          <span><span className="font-mono text-foreground">{totalMatches}</span> matches</span>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={(v) => setFilter(v as FilterType)}>
            <SelectTrigger className="h-7 w-[100px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All</SelectItem>
              <SelectItem value="rules" className="text-xs">Your Rules</SelectItem>
              <SelectItem value="learned" className="text-xs">Learned</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={handleApplyRules}
            disabled={isApplyingRules || enabledRulesCount === 0}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 disabled:opacity-50"
          >
            {isApplyingRules ? <IconLoader2 className="h-3 w-3 animate-spin" /> : <IconPlayerPlay className="h-3 w-3" />}
            Apply Rules
          </button>
          <button
            onClick={openCreateRuleDialog}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <IconPlus className="h-3 w-3" />
            Create Rule
          </button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <IconLoader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="divide-y divide-border/20">
          {/* User Rules */}
          {showRules && rules.map((rule) => (
            <div
              key={`rule-${rule.id}`}
              className={`py-2.5 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] ${!rule.is_enabled ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleToggleRule(rule)}
                  disabled={togglingId === rule.id}
                  className="shrink-0"
                >
                  {togglingId === rule.id ? (
                    <IconLoader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : rule.is_enabled ? (
                    <IconToggleRight className="h-4 w-4 text-primary" />
                  ) : (
                    <IconToggleLeft className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>

                <IconBolt className="h-3 w-3 text-amber-500 shrink-0" />

                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium">{rule.name}</span>
                  <p className="text-[11px] text-muted-foreground truncate">"{rule.description}"</p>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {rule.actionConfig.labelIds?.slice(0, 2).map(id => {
                    const label = getLabelById(id);
                    return label ? (
                      <span
                        key={id}
                        className="text-[10px] px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: label.color + '20', color: label.color }}
                      >
                        {label.name}
                      </span>
                    ) : null;
                  })}
                  {rule.actionConfig.category && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted">
                      {CATEGORY_LABELS[rule.actionConfig.category]}
                    </span>
                  )}
                </div>

                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 shrink-0">
                  Your Rule
                </span>

                <span className="text-xs text-muted-foreground font-mono w-12 text-right shrink-0">
                  {rule.times_matched || 0}×
                </span>

                <button onClick={() => openEditRuleDialog(rule)} className="text-muted-foreground hover:text-foreground p-1">
                  <IconPencil className="h-3 w-3" />
                </button>
                <button onClick={() => setDeleteConfirmRule(rule)} className="text-muted-foreground hover:text-destructive p-1">
                  <IconTrash className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}

          {/* Sender Label Rules */}
          {showLearned && senderLabelRules.map((rule) => (
            <div
              key={`sender-label-${rule.id}`}
              className="py-2.5 hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
            >
              <div className="flex items-center gap-3">
                <div className="w-4" /> {/* Spacer for alignment */}
                <IconMail className="h-3 w-3 text-blue-500 shrink-0" />
                <span className="text-xs truncate max-w-[180px]" title={rule.sender_email}>{rule.sender_email}</span>
                <IconArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-blue-500 text-white shrink-0">
                  {rule.label_name}
                </span>
                <span className="flex-1" />
                <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
                  rule.source === 'user_correction'
                    ? 'bg-amber-500/10 text-amber-600'
                    : rule.evidence_type === 'gmail_filter'
                    ? 'bg-purple-500/10 text-purple-600'
                    : 'bg-blue-500/10 text-blue-600'
                }`}>
                  {rule.source === 'user_correction' ? 'Your Rule' :
                   rule.evidence_type === 'gmail_filter' ? 'Gmail Filter' : 'Learned'}
                </span>
                {rule.evidence_description && (
                  <Tooltip
                    content={
                      <>
                        <p className="text-xs">{rule.evidence_description}</p>
                        {rule.evidence_sample_subjects && (
                          <p className="text-xs opacity-70 mt-1">
                            Examples: {rule.evidence_sample_subjects}
                          </p>
                        )}
                      </>
                    }
                  >
                    <button className="p-0.5 text-muted-foreground hover:text-foreground">
                      <IconInfoCircle className="h-3 w-3" />
                    </button>
                  </Tooltip>
                )}
                <span className="text-xs text-muted-foreground font-mono w-12 text-right shrink-0">
                  {rule.times_applied}×
                </span>
                <button onClick={() => { setEditingSenderRule(rule); setEditSenderRuleLabelName(rule.label_name); }} className="text-muted-foreground hover:text-foreground p-1">
                  <IconPencil className="h-3 w-3" />
                </button>
                <button onClick={() => setDeleteConfirmSenderLabel(rule)} className="text-muted-foreground hover:text-destructive p-1">
                  <IconTrash className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}

          {/* Sender Preferences */}
          {showLearned && senderPreferences.map((pref) => (
            <div
              key={`pref-${pref.id}`}
              className="py-2.5 hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
            >
              <div className="flex items-center gap-3">
                <div className="w-4" />
                <IconMail className="h-3 w-3 text-muted-foreground shrink-0" />
                <span className="text-xs truncate max-w-[180px]" title={pref.sender_pattern}>{pref.sender_pattern}</span>
                <IconArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                <Select
                  value={pref.preferred_category}
                  onValueChange={(value) => handlePrefCategoryChange(pref, value as CategoryType)}
                  disabled={updatingPrefId === pref.id}
                >
                  <SelectTrigger className="h-6 w-[100px] text-xs">
                    <SelectValue>
                      <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${CATEGORY_COLORS[pref.preferred_category]}`} />
                      {CATEGORY_LABELS[pref.preferred_category]}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(CATEGORY_LABELS) as CategoryType[]).map((cat) => (
                      <SelectItem key={cat} value={cat} className="text-xs">
                        <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${CATEGORY_COLORS[cat]}`} />
                        {CATEGORY_LABELS[cat]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {updatingPrefId === pref.id && <IconLoader2 className="h-3 w-3 animate-spin" />}
                <span className="flex-1" />
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-600 shrink-0">
                  Learned Category
                </span>
                <span className="text-xs text-muted-foreground font-mono w-12 text-right shrink-0">
                  {pref.times_applied}×
                </span>
                <button onClick={() => setDeleteConfirmPref(pref)} className="text-muted-foreground hover:text-destructive p-1">
                  <IconTrash className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}

          {/* Empty state */}
          {!showRules && !showLearned && (
            <div className="text-center py-8 text-xs text-muted-foreground">
              No automation found for this filter.
            </div>
          )}
          {showRules && rules.length === 0 && !showLearned && (
            <div className="text-center py-8 text-xs text-muted-foreground">
              No rules yet. Create one to automate email organization.
            </div>
          )}
          {showLearned && senderLabelRules.length === 0 && senderPreferences.length === 0 && !showRules && (
            <div className="text-center py-8 text-xs text-muted-foreground">
              No learned behaviors yet. Move emails or correct labels to teach the system.
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Rule Dialog */}
      <Dialog open={isRuleDialogOpen} onOpenChange={setIsRuleDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Rule' : 'Create Rule'}</DialogTitle>
            <DialogDescription>
              Write a rule in plain English. AI will interpret it during email sync.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Rule Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Travel Emails, Bank Statements"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Rule (in plain English)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., If email is from Uber or Lyft, this is a travel email"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>When this rule matches:</Label>
              <Select value={actionType} onValueChange={(v) => setActionType(v as 'label' | 'category')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="label">Apply label(s)</SelectItem>
                  <SelectItem value="category">Set category</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {actionType === 'label' && (
              <div className="space-y-2">
                <Label>Select Labels</Label>
                {labels.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No labels yet. <button onClick={() => navigate({ to: '/settings', search: { section: 'labels' } })} className="text-primary underline">Create labels first</button>
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {labels.map((label) => (
                      <button
                        key={label.id}
                        type="button"
                        onClick={() => {
                          if (selectedLabelIds.includes(label.id)) {
                            setSelectedLabelIds(selectedLabelIds.filter(id => id !== label.id));
                          } else {
                            setSelectedLabelIds([...selectedLabelIds, label.id]);
                          }
                        }}
                        className={`px-2 py-1 rounded text-xs border transition-colors ${
                          selectedLabelIds.includes(label.id)
                            ? 'border-transparent text-white'
                            : 'border-border bg-background hover:bg-muted'
                        }`}
                        style={selectedLabelIds.includes(label.id) ? { backgroundColor: label.color } : undefined}
                      >
                        {label.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {actionType === 'category' && (
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="do_nothing">Inbox</SelectItem>
                    <SelectItem value="archive">Archive</SelectItem>
                    <SelectItem value="delete">Delete</SelectItem>
                    <SelectItem value="unsubscribe">Unsubscribe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRuleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRuleSubmit} disabled={isSaving}>
              {isSaving && <IconLoader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingRule ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sender Label Rule Dialog */}
      <Dialog open={!!editingSenderRule} onOpenChange={() => setEditingSenderRule(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Sender Rule</DialogTitle>
            <DialogDescription>Change the label for {editingSenderRule?.sender_email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="senderRuleLabel">Label Name</Label>
              <Input
                id="senderRuleLabel"
                value={editSenderRuleLabelName}
                onChange={(e) => setEditSenderRuleLabelName(e.target.value)}
                placeholder="e.g., Invoice, Newsletter"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSenderRule(null)}>Cancel</Button>
            <Button onClick={handleUpdateSenderRule} disabled={isSaving || !editSenderRuleLabelName.trim()}>
              {isSaving && <IconLoader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Rule Confirmation */}
      <AlertDialog open={!!deleteConfirmRule} onOpenChange={() => setDeleteConfirmRule(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{deleteConfirmRule?.name}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRule} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Sender Label Rule Confirmation */}
      <AlertDialog open={!!deleteConfirmSenderLabel} onOpenChange={() => setDeleteConfirmSenderLabel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sender Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Delete rule for "{deleteConfirmSenderLabel?.sender_email}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSenderRule} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Preference Confirmation */}
      <AlertDialog open={!!deleteConfirmPref} onOpenChange={() => setDeleteConfirmPref(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Preference</AlertDialogTitle>
            <AlertDialogDescription>
              Remove preference for "{deleteConfirmPref?.sender_pattern}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePref} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
